import type { UserOnboardingStateResponseDtoCurrentStepEnumKey } from "~/gen/api/types/UserOnboardingStateResponseDto"

export type SupplierOnboardingStepId =
  UserOnboardingStateResponseDtoCurrentStepEnumKey

export type SupplierOnboardingStepDef = Readonly<{
  id: SupplierOnboardingStepId
  stepNumber: number
}>

export type SupplierOnboardingStep = Readonly<{
  id: SupplierOnboardingStepId
  stepNumber: number
  /** Short label shown in the stepper rail. */
  name: string
  /** Panel heading shown below the eyebrow. */
  title: string
  /** Optional helper copy under the panel heading. */
  subtitle: string
}>

export const SUPPLIER_ONBOARDING_STEP_IDS = {
  welcome: "welcome",
  verify_company: "verify_company",
  register_bp: "register_bp",
  my_facilities: "my_facilities",
  review: "review",
} as const

export const SUPPLIER_ONBOARDING_STEP_DEFS: readonly SupplierOnboardingStepDef[] =
  [
    { id: SUPPLIER_ONBOARDING_STEP_IDS.welcome, stepNumber: 1 },
    { id: SUPPLIER_ONBOARDING_STEP_IDS.verify_company, stepNumber: 2 },
    { id: SUPPLIER_ONBOARDING_STEP_IDS.register_bp, stepNumber: 3 },
    { id: SUPPLIER_ONBOARDING_STEP_IDS.my_facilities, stepNumber: 4 },
    { id: SUPPLIER_ONBOARDING_STEP_IDS.review, stepNumber: 5 },
  ] as const

const STEP_INDEX_BY_ID: ReadonlyMap<SupplierOnboardingStepId, number> = new Map(
  SUPPLIER_ONBOARDING_STEP_DEFS.map((def, index) => [def.id, index])
)

export const getStepIndex = (id: SupplierOnboardingStepId): number =>
  STEP_INDEX_BY_ID.get(id) ?? -1

export const isStepAtOrAfter = (
  step: SupplierOnboardingStepId,
  threshold: SupplierOnboardingStepId
): boolean => getStepIndex(step) >= getStepIndex(threshold)

export const getNextStepId = (
  id: SupplierOnboardingStepId
): SupplierOnboardingStepId | null =>
  SUPPLIER_ONBOARDING_STEP_DEFS[getStepIndex(id) + 1]?.id ?? null

export const getPreviousStepId = (
  id: SupplierOnboardingStepId
): SupplierOnboardingStepId | null =>
  SUPPLIER_ONBOARDING_STEP_DEFS[getStepIndex(id) - 1]?.id ?? null

export const resolveSupplierOnboardingStepId = (
  currentStep: SupplierOnboardingStepId | null | undefined
): SupplierOnboardingStepId => {
  return currentStep ?? SUPPLIER_ONBOARDING_STEP_IDS.welcome
}
